import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import LayoutGuest from '../../components/layouts/LayoutGuest';
import NotFoundPage from '../../routes/NotFoundPage';
import useServices from '../../stores/useServices';
import Skeleton from '../../components/ui/skeleton';
import ScrollableWrapper from '../../components/ScrollableWrapper';
import Input from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { PromoSlider } from './component/PromoSlider';
import { OtherSlider } from './component/OtherSlider';
import { TourSlider } from './component/TourSlider';
import { InsuranceSlider } from './component/InsuranceSlider';
import useCompanyProfile from '../../stores/useCompanyProfiles';
import { LanguageContext } from '../../contexts/LanguageContext';

export default function DetailService() {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const { fetchLoading, fetchServices, services } = useServices();
  const { profile } = useCompanyProfile();
  const { id } = useParams();
  const [notFound, setNotFound] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm();

  const paramId = Number(id);
  const serviceById = services.find((service) => service.service_id === paramId);

  const showTourSlider = () => {
    const nameEn = serviceById?.name_srv_en?.toLowerCase() || '';
    const nameId = serviceById?.name_srv_id?.toLowerCase() || '';
    return nameEn.includes('tour') || nameId.includes('tour') || nameId.includes('tur');
  };

  const showInsuranceSlider = () => {
    const nameEn = serviceById?.name_srv_en?.toLowerCase() || '';
    const nameId = serviceById?.name_srv_id?.toLowerCase() || '';
    return nameEn.includes('insurance') || nameId.includes('insurance') || nameId.includes('asuransi');
  };

  useEffect(() => {
    if (services.length === 0) {
      fetchServices();
    }
    if (services.length > 0 && !serviceById) setNotFound(true);
  }, [fetchServices, serviceById, services]);

  useEffect(() => {
    if (services.length > 0 && !paramId) {
      const firstServiceId = services[0].service_id;
      navigate(`/service/${firstServiceId}`, { replace: true });
      setNotFound(false);
    }
  }, [services, navigate, paramId]);

  useEffect(() => {
    if (scrollRef.current && services.length > 0 && paramId) {
      const activeLink = scrollRef.current.querySelector(`[data-service-id="${paramId}"]`);
      if (activeLink instanceof HTMLElement) {
        const scrollPosition = activeLink.offsetLeft;
        scrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }
  }, [services, paramId]);

  const handleLinkClick = (serviceId: number) => {
    navigate(`/service/${serviceId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (notFound) return <NotFoundPage />;

  // handle submit form pemesanan

  const onSubmit = async (data: any) => {
    const message = lang
      ? `Halo Saya *${data.name}*.
  
  Saya ingin memesan layanan detail kebutuhan berikut:
  ${data.needs}
  
  Mohon informasi lebih lanjut. Terima kasih!`
      : `Hello, I am *${data.name}*.

I would like to order the following detailed service requirements:
${data.needs}

Please provide more information. Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = `+62${profile?.whatsapp_pr}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    console.log('Form Data:', data);
    console.log('WhatsApp URL:', encodedMessage);
    window.open(whatsappUrl, '_blank');
  };

  return (
    <LayoutGuest className="max-w-screen-xl px-2">
      {fetchLoading ? (
        <Skeleton className="h-full min-h-[40vh] p-4 gap-3 flex flex-col justify-end w-full">
          <Skeleton className="max-w-64 bg-neutral-200 w-full h-8" />
          <Skeleton className="max-w-96 bg-neutral-200 w-full h-4" />
        </Skeleton>
      ) : (
        <div className="flex flex-col gap-4">
          <header
            style={{ backgroundImage: `url(${serviceById?.image_srv})` }}
            className="rounded-lg transition-all mt-[16px] bg-center duration-700 flex bg-cover bg-blue-50 overflow-hidden"
          >
            <div className="flex rounded-lg text-white justify-between font-medium w-full h-full min-h-[40vh] p-4 backdrop-blur-sm bg-gradient-to-r from-primary to-transparent gap-3 flex-col">
              <ScrollableWrapper ref={scrollRef} className="hide-scrollbar sm:gap-4 sm:text-md text-sm gap-3 flex">
                {services.map((srv) => (
                  <button
                    type="button"
                    key={srv.service_id}
                    data-service-id={srv.service_id}
                    onClick={() => handleLinkClick(srv.service_id)}
                    className={`inline-block flex-shrink-0 font-semibold hover:text-secondary cursor-pointer ${
                      srv.service_id === paramId ? 'text-secondary' : 'text-white'
                    }`}
                  >
                    {lang ? srv.name_srv_id : srv.name_srv_en || srv.name_srv_id}
                  </button>
                ))}
              </ScrollableWrapper>
              <div key={`detail-${paramId}`}>
                <h1 className="font-raleway md:text-3xl text-2xl font-bold animate-slide-in-left drop-shadow-md">
                  {lang ? serviceById?.name_srv_id : serviceById?.name_srv_en || serviceById?.name_srv_id}
                </h1>
                <p className="animate-slide-in-left duration-1000 text-sm sm:text-base">
                  {lang
                    ? serviceById?.description_srv_id
                    : serviceById?.description_srv_en || serviceById?.description_srv_id}
                </p>
              </div>
            </div>
          </header>
          <PromoSlider key={`${paramId}-promoSlide`} className="animate-fade-in-down" />
          <OtherSlider key={`${paramId}-otherSlide`} className="animate-fade-in-down" />
          {showTourSlider() && <TourSlider key={`${paramId}-tourSlide`} className="animate-fade-in-down" />}
          {showInsuranceSlider() && (
            <InsuranceSlider key={`${paramId}-insuranceSide`} className="animate-fade-in-down" />
          )}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-[url(/assets/BackgroundForm.png)] flex md:flex-row flex-col bg-cover justify-center items-center md:gap-8 gap-4 p-4 rounded-lg my-4 md:my-8"
          >
            <div className=" drop-shadow-md text-2xl w-72 flex font-bold bg-gradient-to-r from-white bg-clip-text text-transparent to-currency flex-col gap-4 text-center">
              <img className="animate-float md:block hidden" src="/assets/ChatBubble.svg" alt="" />
              <h4>{t('form.title')}</h4>
            </div>
            <div className="flex flex-col text-sm sm:text-md rounded-lg gap-4 bg-white w-full max-w-96 p-4">
              <div className="flex flex-col gap-2">
                <span className="flex gap-1">
                  {t('form.name')} <p className="text-red-600">*</p>
                </span>
                <Input {...register('name')} required type="text" placeholder="Johan Setiawan .." />
              </div>
              {/* <div className="flex flex-col gap-2">
                <span className="flex gap-1">{t('form.organization')}</span>
                <Input {...register('instance')} type="text" placeholder="PT Mulia Sujar abadi .." />
                <i className="text-secondNeutral text-xs sm:text-sm">*{t('form.note')}</i>
              </div> */}
              <div className="flex flex-col gap-2">
                <span className="flex gap-1">
                  {t('form.need')}
                  <p className="text-red-600">*</p>
                </span>
                <Textarea {...register('needs')} required className="text-sm" placeholder={t('form.placeholder')} />
              </div>
              <Button type="submit" variant="secondaryBackground" className="w-full flex items-center gap-2">
                <img src="/assets/Whatsapp.svg" alt="whatsapp logo" />
                {t('form.button')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </LayoutGuest>
  );
}
